import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import './App.css';
import BookOverview from './components/BookOverview';
import ChapterNav from './components/ChapterNav';
import ChapterView from './components/ChapterView';
import BookLibrary from './components/BookLibrary';
import PromptBuilder from './components/PromptBuilder';
import { AuthStatus } from './components/AuthComponents';
import { DynamicBooksService } from './services/PaiPlatformService';
import BuildBookModal from './components/BuildBookModal';

const BLOB_BASE_URL = 'https://paiwizsa.blob.core.windows.net/dynabooks';

function BookBrowser() {
    // Authentication state
    const isAuthenticated = useIsAuthenticated();
    
    // Mobile detection (simple window width check)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= 600);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
  // User's books from API
  const [userBooks, setUserBooks] = useState([]);
  const [userBooksLoading, setUserBooksLoading] = useState(false);
  const [userBooksError, setUserBooksError] = useState(null);
  
  // Build Book modal state
  const [showBuildModal, setShowBuildModal] = useState(false);

  // Image lightbox state
  const [lightboxSrc, setLightboxSrc] = useState(null);
  
  const [bookId, setBookId] = useState('');
  const [puzzleBookId, setPuzzleBookId] = useState(''); // bookId for puzzle querystring
  const [localFolder, setLocalFolder] = useState(null); // Track local folder for assets
  const [bookData, setBookData] = useState(null);
  const [keywordsData, setKeywordsData] = useState(null);
  
  // Supplementary chapter data from separate JSON files
  const [actionsData, setActionsData] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [expertsData, setExpertsData] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('overview'); // 'overview' or 'chapter'

  // Prompt Builder state
  const [promptPersona, setPromptPersona] = useState('');
  const [promptMetaPrompt, setPromptMetaPrompt] = useState('');
  const [promptKeywords, setPromptKeywords] = useState('');
  const [includeBookTitle, setIncludeBookTitle] = useState(true);

  // Handle book selection from library - always load from remote blob storage
  const handleSelectBook = (book) => {
    // Use bookId for remote loading (from books.json manifest)
    // Falls back to blobFolder or folder if bookId not available
    const remoteId = book.bookId || book.blobFolder || book.folder;
    if (remoteId) {
      setBookId(remoteId);
      setPuzzleBookId(remoteId);
      loadBookFromRemote(remoteId);
    }
  };

  // Load book from remote blob storage (used with ?puzzle= querystring)
  const loadBookFromRemote = async (blobFolder) => {
    if (!blobFolder.trim()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const basePath = `${BLOB_BASE_URL}/${blobFolder}`;
      const url = `${basePath}/paitar.json`;
      const keywordsUrl = `${basePath}/keywords.json`;
      const actionsUrl = `${basePath}/actions.json`;
      const quizUrl = `${basePath}/quiz.json`;
      const expertsUrl = `${basePath}/experts.json`;
      
      const [bookResponse, keywordsResponse, actionsResponse, quizResponse, expertsResponse] = await Promise.all([
        fetch(url),
        fetch(keywordsUrl).catch(() => null),
        fetch(actionsUrl).catch(() => null),
        fetch(quizUrl).catch(() => null),
        fetch(expertsUrl).catch(() => null)
      ]);
      
      if (!bookResponse.ok) {
        throw new Error(`Failed to load book: ${bookResponse.status} ${bookResponse.statusText}`);
      }
      
      const data = await bookResponse.json();
      setBookData(data);
      setLocalFolder(null); // null indicates remote loading - use BLOB_BASE_URL for assets
      setBookId(blobFolder); // Store the blob folder for asset URLs
      setPuzzleBookId(blobFolder); // Use the blobFolder as the puzzle bookId for remote loading
      
      // Initialize prompt builder with book data
      setPromptPersona(data.Persona || '');
      setPromptMetaPrompt(data.BookMetaPrompt || '');
      setPromptKeywords('');
      
      if (keywordsResponse && keywordsResponse.ok) {
        const keywords = await keywordsResponse.json();
        setKeywordsData(keywords);
      } else {
        setKeywordsData(null);
      }
      
      // Load supplementary chapter data
      if (actionsResponse && actionsResponse.ok) {
        const actions = await actionsResponse.json();
        setActionsData(actions);
      } else {
        setActionsData(null);
      }
      
      if (quizResponse && quizResponse.ok) {
        const quiz = await quizResponse.json();
        setQuizData(quiz);
      } else {
        setQuizData(null);
      }
      
      if (expertsResponse && expertsResponse.ok) {
        const experts = await expertsResponse.json();
        setExpertsData(experts);
      } else {
        setExpertsData(null);
      }
      
      setSelectedChapter(null);
      setActiveView('overview');
    } catch (err) {
      setError(`Error loading book: ${err.message}`);
      setBookData(null);
    } finally {
      setLoading(false);
    }
  };

  // Check for bookId in URL query string on mount (case-insensitive)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryBookId = urlParams.get('bookId') || urlParams.get('bookid');
    
    if (queryBookId) {
      // Load from remote blob storage
      setBookId(queryBookId);
      loadBookFromRemote(queryBookId);
    }
  }, []);

  useEffect(() => {
    if (!bookData || loading) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [bookData, loading]);
  
  // Fetch user's books when authenticated
  useEffect(() => {
    const fetchUserBooks = async () => {
      if (!isAuthenticated) {
        setUserBooks([]);
        return;
      }
      
      setUserBooksLoading(true);
      setUserBooksError(null);
      
      try {
        const books = await DynamicBooksService.getBooks();
        setUserBooks(books || []);
      } catch (err) {
        console.error('Failed to fetch user books:', err);
        setUserBooksError(err.message);
        setUserBooks([]);
      } finally {
        setUserBooksLoading(false);
      }
    };
    
    fetchUserBooks();
  }, [isAuthenticated]);
  
  // Handle selecting a user book from API
  const handleSelectUserBook = (book) => {
    // Use storageFolder for remote loading (this is the blob storage folder name)
    // Falls back to bookId, blobFolder, or id if storageFolder not available
    const remoteId = book.storageFolder || book.bookId || book.blobFolder || book.id;
    if (remoteId) {
      setBookId(remoteId);
      setPuzzleBookId(remoteId);
      loadBookFromRemote(remoteId);
    }
  };
  
  // Handle Build Book button click
  const handleBuildBook = () => {
    setShowBuildModal(true);
  };
  
  // Handle successful book creation
  const handleBookCreated = (newBook) => {
    // Refresh the user books list
    if (isAuthenticated) {
      DynamicBooksService.getBooks()
        .then(books => setUserBooks(books || []))
        .catch(err => console.error('Failed to refresh books:', err));
    }
  };

  // Load book data from remote blob storage
  const loadBookFromBlob = async () => {
    if (!bookId.trim()) {
      setError('Please enter a Book ID');
      return;
    }
    await loadBookFromRemote(bookId);
  };

  // Load test data from local file
  const loadTestData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [bookResponse, keywordsResponse] = await Promise.all([
        fetch('/data/paitar.json'),
        fetch('/data/keywords.json').catch(() => null)
      ]);
      
      if (!bookResponse.ok) {
        throw new Error(`Failed to load test data: ${bookResponse.status}`);
      }
      
      const data = await bookResponse.json();
      setBookData(data);
      setLocalFolder(''); // Test data is in /data/ root
      
      if (keywordsResponse && keywordsResponse.ok) {
        const keywords = await keywordsResponse.json();
        setKeywordsData(keywords);
      } else {
        setKeywordsData(null);
      }
      
      setSelectedChapter(null);
      setActiveView('overview');
    } catch (err) {
      setError(`Error loading test data: ${err.message}`);
      setBookData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle chapter selection
  const handleChapterSelect = (chapter) => {
    setSelectedChapter(chapter);
    setActiveView('chapter');
  };

  // Handle back to overview
  const handleBackToOverview = () => {
    setSelectedChapter(null);
    setActiveView('overview');
  };

  // Handle Enter key in textbox
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      loadBookFromBlob();
    }
  };

  // Handle keyword click - add to prompt builder
  const handleKeywordClick = (keyword) => {
    setPromptKeywords(prev => {
      if (!prev.trim()) return keyword;
      // Don't add duplicate keywords
      if (prev.toLowerCase().includes(keyword.toLowerCase())) return prev;
      return `${prev}, ${keyword}`;
    });
  };

  // Handler to go back to home/library
  const handleBackToHome = () => {
    setBookId('');
    setPuzzleBookId('');
    setLocalFolder(null);
    setBookData(null);
    setKeywordsData(null);
    setSelectedChapter(null);
    setError(null);
    setActiveView('overview');
    // Reset prompt builder
    setPromptPersona('');
    setPromptMetaPrompt('');
    setPromptKeywords('');
    // Clear querystring from URL
    if (window.history.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  return (
    <div className="app">
      {/* HEADER */}
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">📚 Paitar Book Browser</h1>
          <div className="header-right">
            <div className="book-id-input-group">
            {/* Mobile: Show chapter dropdown if book loaded, else search */}
            {isMobile && bookData && bookData.Chapters && bookData.Chapters.length > 0 ? (
              <select
                className="chapter-dropdown"
                value={selectedChapter ? selectedChapter.ChapterNumber : 'overview'}
                onChange={e => {
                  if (e.target.value === 'overview') {
                    handleBackToOverview();
                  } else {
                    const chapterNum = Number(e.target.value);
                    const chapter = bookData.Chapters.find(c => c.ChapterNumber === chapterNum);
                    if (chapter) handleChapterSelect(chapter);
                  }
                }}
                style={{ width: '100%', padding: 10, borderRadius: 8, fontSize: 16 }}
              >
                <option value="overview">Book Overview</option>
                {bookData.Chapters.map(ch => (
                  <option key={ch.ChapterNumber} value={ch.ChapterNumber}>
                    Chapter {ch.ChapterNumber}: {ch.Title}
                  </option>
                ))}
              </select>
            ) : (!bookData || loading ? (
              <>
                <input
                  type="text"
                  className="book-id-input"
                  placeholder="Enter Book ID and press ENTER (e.g., reengineer-3f6bc665cbfe)"
                  value={bookId}
                  onChange={(e) => setBookId(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
                <button 
                  className="search-btn"
                  onClick={loadBookFromBlob}
                  disabled={loading}
                  title="Search for Book"
                >
                  🔍
                </button>
                <button 
                  className="build-btn"
                  onClick={handleBuildBook}
                  disabled={loading}
                >
                  🛠️ Build Book
                </button>
              </>
            ) : null)}
            {bookData && !loading && (
              <button 
                className="back-btn"
                onClick={handleBackToHome}
                title="Back to Library"
                style={{marginLeft: 8}}
              >
                ← Back to Library
              </button>
            )}
            </div>
            <AuthStatus />
          </div>
        </div>
      </header>

      {/* BODY */}
      <div className="app-body">
        {/* MAIN SECTION (85%) */}
        <main className="main-section">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {loading && (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading book data...</p>
            </div>
          )}

          {!bookData && !loading && !error && (
            <div className="home-library-section">
              <div className="welcome-header">
                <div className="welcome-announcement">
                  <div className="announcement-label">FROM THE CREATOR OF BRAINDANCE.COM</div>
                  <div className="announcement-body">
                    <img src="/img/dynamicbookslogo.png" alt="Dynamic Books" className="welcome-image" onClick={() => setLightboxSrc('/img/BrainDanceFamily.png')} />
                    <div className="announcement-text">
                      <h2>Discover Dynamic Books</h2>
                      <p>Interactive, AI-enabled books that make reading more engaging and personal.<br/>
                      Authors can publish their derivative works to the <a href="https://braindance.net" rel="noopener noreferrer">BrainDance Store</a>.</p>
                    </div>
                    <a href="https://DynamicBooks.net" target="_blank" rel="noopener noreferrer" className="announcement-cta">
                      Visit DynamicBooks.net &rarr;
                    </a>
                  </div>
                </div>
              </div>
              <div className="welcome-row">
                  <div className="welcome-copy">
                    <div className="welcome-header">
                      <h2>Welcome to the Paitar Dynamic Book Browser and Authoring Platform</h2>
                      <p>Search and select a book from the library below, or enter a Book ID above.</p>
                    </div>
                  </div>
                  <div className="welcome-video">
                    <section className="video-section">
                      <div className="video-container">
                        <iframe
                          src="https://www.youtube.com/embed/UPTClo-UnCw?si=1OyRiwt6pzzQfXGA"
                          title="Paitar Dynamic Books introduction"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          loading="lazy"
                        />
                      </div>
                    </section>
                  </div>
              </div>
              {/* User's Books Section - shown when authenticated */}
              {isAuthenticated && (
                <div className="user-books-section">
                  <h3 className="user-books-header">📚 My Books</h3>
                  {userBooksLoading && (
                    <div className="user-books-loading">
                      <div className="loading-spinner small"></div>
                      <span>Loading your books...</span>
                    </div>
                  )}
                  {userBooksError && (
                    <div className="user-books-error">
                      <span>⚠️ {userBooksError}</span>
                    </div>
                  )}
                  {!userBooksLoading && !userBooksError && userBooks.length === 0 && (
                    <div className="user-books-empty">
                      <p>You haven't created any books yet.</p>
                      <button className="build-btn" onClick={handleBuildBook}>
                        🛠️ Build Your First Book
                      </button>
                    </div>
                  )}
                  {!userBooksLoading && userBooks.length > 0 && (
                    <div className="books-grid">
                      {userBooks.map((book) => (
                        <div 
                          key={book.id} 
                          className="book-card"
                          onClick={() => handleSelectUserBook(book)}
                        >
                          <div className="row">
                            <div className="col-4">
                              <div className="book-thumbnail">
                                {book.infographicUrl ? (
                                  <img 
                                    src={book.infographicUrl} 
                                    alt={`${book.bookName} infographic`}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className="thumbnail-placeholder" style={{ display: book.infographicUrl ? 'none' : 'flex' }}>
                                  <span>📚</span>
                                </div>
                              </div>
                            </div>
                            <div className="col-8">
                              <div className="book-info">
                                <h3 className="book-title">{book.bookName}</h3>
                                <p className="book-author">by {book.bookAuthor}</p>
                                <p className="book-description">{book.description || book.bookPersona || ''}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <BookLibrary onSelectBook={handleSelectBook} />
            </div>
          )}

          {bookData && !loading && activeView === 'overview' && (
            <div className="book-content">
              <h2 className="book-title">{bookData.BookTitle}</h2>
              <p className="book-author">by {bookData.BookAuthor}</p>
              <BookOverview 
                bookData={{
                  ...bookData,
                  Author: bookData.BookAuthor,
                  ImagePath: bookData.CoverImageUrl,
                  InfographicUrl: bookData.InfographicUrl,
                  SocialMediaLinks: bookData.SocialMediaLinks
                }} 
                keywords={keywordsData?.keywords || []}
                cardHeight={480}
                onKeywordClick={handleKeywordClick}
              />
              
              {bookData.Crossword && (
                <div className="crossword-section">
                  <div className="crossword-layout">
                    <div className="crossword-image-container">
                      <a
                        href={`https://puzzlearn.net/?puzzle=${encodeURIComponent(puzzleBookId || bookId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src="/img/crossword.png"
                          alt="Crossword Puzzle"
                          className="crossword-image-responsive"
                        />
                      </a>
                    </div>
                    <div className="crossword-info-container">
                      <h3>📝 {bookData.Crossword.title}</h3>
                      <div className="crossword-info">
                        <span>Grid Size: {bookData.Crossword.gridSize?.rows} x {bookData.Crossword.gridSize?.cols}</span>
                        <span>{bookData.Crossword.words?.length} words to discover</span>
                      </div>
                      <div className="open-puzzle-link-container" style={{ marginBottom: 12 }}>
                        <a
                          href={`https://puzzlearn.net/?puzzle=${encodeURIComponent(puzzleBookId || bookId)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="open-puzzle-link"
                          style={{
                            display: 'inline-block',
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            textDecoration: 'none',
                            borderRadius: 6,
                            fontWeight: 600,
                            fontSize: 14
                          }}
                        >
                          🧩 Open Puzzle in New Window
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {bookData && !loading && activeView === 'chapter' && selectedChapter && (
            <ChapterView 
              chapter={selectedChapter} 
              onBack={handleBackToOverview}
              bookTitle={bookData.BookTitle}
              promptContext={{
                persona: promptPersona,
                metaPrompt: promptMetaPrompt,
                keywords: promptKeywords,
                includeBookTitle: includeBookTitle
              }}
              actionsData={actionsData}
              quizData={quizData}
              expertsData={expertsData}
            />
          )}
        </main>

        {/* RIGHT SECTION (15%) */}
        <aside className="right-section">
          {bookData && (
            <ChapterNav 
              chapters={bookData.Chapters || []}
              selectedChapter={selectedChapter}
              onChapterSelect={handleChapterSelect}
              onOverviewClick={handleBackToOverview}
              isOverviewActive={activeView === 'overview'}
            >
              <PromptBuilder
                persona={promptPersona}
                setPersona={setPromptPersona}
                metaPrompt={promptMetaPrompt}
                setMetaPrompt={setPromptMetaPrompt}
                selectedKeywords={promptKeywords}
                setSelectedKeywords={setPromptKeywords}
                onClearKeywords={() => setPromptKeywords('')}
                bookTitle={bookData?.BookTitle}
                includeBookTitle={includeBookTitle}
                setIncludeBookTitle={setIncludeBookTitle}
              />
            </ChapterNav>
          )}
          
          {!bookData && (
            <div className="nav-placeholder">
              <p>📖</p>
              <p>Load a book to see chapters</p>
            </div>
          )}
        </aside>
      </div>
      
      {/* Image Lightbox */}
      {lightboxSrc && (
        <div className="lightbox-overlay" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="Zoomed" className="lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setLightboxSrc(null)}>✕</button>
        </div>
      )}

      {/* Build Book Modal */}
      <BuildBookModal 
        isOpen={showBuildModal}
        onClose={() => setShowBuildModal(false)}
        onSuccess={handleBookCreated}
      />
      
      {/* Footer */}
     
<footer class="site-footer">
  <div class="wrap site-footer-wrap">
    <div class="site-footer-top">
      <div class="site-footer-brand">
        <div class="site-footer-mark" aria-hidden="true">⊕</div>
        <div>
          <p class="site-footer-title">The Paitars</p>
          <p class="site-footer-tagline">Infotaining songs designed to educate, inspire, and be fun to listen to.</p>
        </div>
      </div>

      <nav class="site-footer-nav" aria-label="Footer">
        <a href="#top">Home</a>
        <a href="#albums">Albums</a>
        <a href="#videos">Videos</a>
        <a href="listen.html">Listen</a>
        <a href="#about">About</a>
      </nav>
    </div>

    <div class="site-footer-meta">
      <p class="site-footer-note">The Paitars provides music, visuals, and educational storytelling inspired by books, causes, places, and the evolving possibilities of human-AI collaboration.</p>
      <p class="copyright">© 2026 The Paitars / BrainDance Music. All rights reserved.</p>
    </div>

    <div class="site-footer-family">
      <div class="site-footer-family-intro">
        <a href="https://braindance.com" target="_blank" rel="noopener noreferrer" class="footer-banner-link">
            <img src="img/bdancebanner.png" alt="BrainDance Family of Websites" class="footer-banner" />
        </a>
        <p>The Paitars is part of the <a href="https://braindance.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">BrainDance Family of Websites</a>, a connected ecosystem of AI tools, learning platforms, and personal-intelligence apps.</p>
      </div>

      <ul class="family-sites" aria-label="BrainDance family of websites">
        <li>
          <a href="https://braindance.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">BrainDance.com</a>
          <p>The umbrella brand for books, tools, apps, and websites focused on human-AI collaboration, personal intelligence, learning, creativity, and cognitive performance.</p>
        </li>
        <li>
          <a href="https://promptfab.ai" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">PromptFab.ai</a>
          <p>A prompt optimizer and prompt pack authoring platform, featuring the proprietary PAIQ rating system for measuring the intelligence behind your prompts.</p>
        </li>
        <li>
          <a href="https://personafab.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">PersonaFab.com</a>
          <p>Build reusable AI personas with actionable specs that drive better prompt engineering, UX decisions, and AI interaction design.</p>
        </li>
        <li>
          <a href="https://workflowfab.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">WorkflowFab.com</a>
          <p>Mapping workflows to your persona use cases.</p>
        </li>
        <li>
          <a href="https://cxfab.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">cxFab.com</a>
          <p>Customer experience UI optimization.</p>
        </li>
        <li>
          <a href="https://claudegen.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">ClaudeGen.com</a>
          <p>Generate Claude constitution files from customer experiences.</p>
        </li>
        <li>
          <a href="https://metakb.com" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">MetaKB.com</a>
          <p>A personalized knowledge and intelligence system for saving, organizing, and interacting with the information that matters to you.</p>
        </li>
        <li>
          <a href="https://dynamicbooks.net" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">DynamicBooks.net</a>
          <p>AI-enriched, interactive books with chapters, quizzes, actions, and puzzles that help you understand and apply more of what you read.</p>
        </li>
        <li>
          <a href="https://quoteminer.net" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">QuoteMiner.net</a>
          <p>A quote discovery and quote-branding platform, tapping into the minds of the great leaders and thinkers of our time.</p>
        </li>
        <li>
          <a href="https://www.braindance.com/braindancecoach" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">BrainDance Coach</a>
          <p>A mobile app focused on micro breaks, movement, and mental clarity.</p>
        </li>
        <li>
          <a href="https://puzzlearn.net" target="_blank" rel="noopener noreferrer" class="footer-link-dark-blue">PuzzLearn.net</a>
          <p>A learning puzzle and crossword platform integrated with DynamicBooks.net.</p>
        </li>
      </ul>
    </div>
  </div>
</footer>
    </div>
  );
}

// Main App with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<BookBrowser />} />
      </Routes>
    </Router>
  );
}

export default App;
